import { Component } from '@theme/component';
import { StandardEvents, CartLinesUpdateEvent } from '@shopify/events';
import { DrawerOpenEvent, DrawerCloseEvent } from '@theme/theme-drawer';

/**
 * Header actions component that manages cart notifications, cart-drawer trigger
 * aria-expanded state, and search modal trigger.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} liveRegion - The live region for cart announcements.
 *
 * @extends {Component<Refs>}
 */
class HeaderActions extends Component {
  requiredRefs = ['liveRegion'];

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(StandardEvents.cartLinesUpdate, this.#onCartUpdate);
    document.addEventListener(DrawerOpenEvent.eventName, this.#onDrawerStateChange);
    document.addEventListener(DrawerCloseEvent.eventName, this.#onDrawerStateChange);

    this.addEventListener('click', this.#onHeaderActionsClick);

    this.#syncCartTriggerAriaExpanded();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#onCartUpdate);
    document.removeEventListener(DrawerOpenEvent.eventName, this.#onDrawerStateChange);
    document.removeEventListener(DrawerCloseEvent.eventName, this.#onDrawerStateChange);

    this.removeEventListener('click', this.#onHeaderActionsClick);
  }

  #syncCartTriggerAriaExpanded = () => {
    const cartDrawer = document.getElementById('cart-drawer');
    if (!cartDrawer) return;

    const trigger = this.querySelector('[aria-controls="cart-drawer"]');
    if (!trigger) return;

    trigger.setAttribute('aria-expanded', cartDrawer.hasAttribute('open') ? 'true' : 'false');
  };

  /**
   * Handles header action clicks.
   * @param {MouseEvent} event
   */
  #onHeaderActionsClick = (event) => {
    const target = /** @type {HTMLElement | null} */ (event.target);
    const searchTrigger = target?.closest('[data-knn-search-open]');

    if (!searchTrigger) return;

    event.preventDefault();
    this.#openSearchModal();
  };

  #openSearchModal = () => {
    const searchModal = document.getElementById('search-modal');
    if (!searchModal) {
      console.warn('[header-actions] Search modal not found.');
      return;
    }

    const dialog = searchModal.matches('dialog')
      ? searchModal
      : searchModal.querySelector('dialog');

    if (typeof searchModal.showDialog === 'function') {
      searchModal.showDialog();
    } else if (typeof searchModal.open === 'function') {
      searchModal.open();
    } else if (dialog && typeof dialog.showModal === 'function') {
      dialog.showModal();
    } else if (dialog) {
      dialog.setAttribute('open', '');
    } else {
      searchModal.setAttribute('open', '');
    }

    window.setTimeout(() => {
      const input = searchModal.querySelector(
        'input[type="search"], input[name="q"], input[type="text"]'
      );

      if (input) input.focus();
    }, 80);
  };

  /**
   * Syncs `aria-expanded` on the cart-drawer trigger when the drawer opens or closes.
   * @param {Event} event
   */
  #onDrawerStateChange = (event) => {
    const target = /** @type {HTMLElement | null} */ (event.target);
    if (target?.id !== 'cart-drawer') return;

    this.#syncCartTriggerAriaExpanded();
  };

  /**
   * Handles cart update events and announces the new count to screen readers.
   * @param {CartLinesUpdateEvent} event
   */
  #onCartUpdate = (event) => {
    event.promise
      ?.then(({ cart }) => {
        const cartCount = cart?.totalQuantity;
        if (cartCount === undefined) return;

        this.refs.liveRegion.textContent = `${Theme.translations.cart_count}: ${cartCount}`;
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') {
          console.warn('[header-actions] Event promise rejected:', error);
        }
      });
  };
}

if (!customElements.get('header-actions')) {
  customElements.define('header-actions', HeaderActions);
}